/**
 * Returns a human-readable string describing how long ago the given date was.
 *
 * @param {string} dateString - A valid date string (e.g. "2023-01-01T10:20:30Z").
 * @returns {string} - A formatted string indicating time elapsed,
 *                     or an error message if the date is invalid.
 */
export function getTimeSince(dateString: string | number): string {
  try {
    const ERROR_INVALID_DATE = 'Invalid date format';
    const SUFFIX_JUST_NOW = 'just now';
    const SUFFIX_AGO = ' ago';

    // Parse the input date
    if (typeof dateString === 'number') {
      dateString = new Date(dateString).toISOString();
    }

    const parsedDate = new Date(Date.parse(dateString));
    if (Number.isNaN(parsedDate.getTime())) {
      return ERROR_INVALID_DATE;
    }

    // Calculate the time difference in milliseconds
    const now = new Date();
    const timeDifferenceInMs = now.getTime() - parsedDate.getTime();
    const absoluteDifferenceInMs = Math.abs(timeDifferenceInMs);

    // Constants for milliseconds in various units
    const MS_PER_MINUTE = 1000 * 60;
    const MS_PER_HOUR = MS_PER_MINUTE * 60;
    const MS_PER_DAY = MS_PER_HOUR * 24;

    // Break down the difference into days, hours, and minutes
    const days = Math.floor(absoluteDifferenceInMs / MS_PER_DAY);
    const hours = Math.floor(absoluteDifferenceInMs / MS_PER_HOUR) % 24;
    const minutes = Math.floor(absoluteDifferenceInMs / MS_PER_MINUTE) % 60;

    // Build and return the appropriate output
    if (days > 0) return `${days}d${SUFFIX_AGO}`;
    if (hours > 0) return `${hours}h${SUFFIX_AGO}`;
    if (minutes > 0) return `${minutes}m${SUFFIX_AGO}`;

    return SUFFIX_JUST_NOW;
  } catch (error) {
    return `Error: ${error.message}`;
  }
}
